import { arrayToString } from "~/utils/arrayToString";

/**
 * Style hooks for Storyblok bloks.
 *
 * Every style field takes its options from a shared Storyblok datasource whose
 * values ARE Tailwind classes (Typography: `text-xl`, `lg:text-4xl text-3xl
 * font-semibold`; Border width: `border-b-2`). A class only exists in the CSS
 * if the template safelists it — see the template's tailwind config, and the
 * datasource CSVs in `templates/<t>/docs/storyblok/datasources/`, which are
 * tested against that safelist.
 */

// Font-size utilities, optionally breakpoint-prefixed: text-xs … text-9xl and
// arbitrary lengths (text-[28px]). Not alignment (text-center) or colour
// (text-white, text-[#fff]).
const FONT_SIZE_CLASS =
	/(^|\s)([a-z0-9-]+:)*text-(xs|sm|base|lg|xl|[2-9]xl|\[\d[^\]]*\])(\s|$)/;

/** Whether a style value sets a font size (vs. only weight or alignment). */
export const hasFontSize = (classes: string): boolean =>
	FONT_SIZE_CLASS.test(classes);

const FONT_WEIGHT_CLASS =
	/(^|\s)font-(thin|extralight|light|normal|medium|semibold|bold|extrabold|black)(\s|$)/;

/**
 * Classes from a single- or multi-option style field, trimmed. Empty
 * string when unset, so callers can `||` their own default.
 */
export const styleClasses = (value?: string[] | string | null): string =>
	(arrayToString(value ?? undefined) ?? "").trim();

/**
 * Typography classes that keep a default weight unless the editor picked
 * one — choosing only a size must not silently drop the bold.
 */
export const typographyWithWeight = (
	value: string[] | string | null | undefined,
	defaultWeight: string
): string => {
	const classes = styleClasses(value);
	if (!classes) return defaultWeight;

	return FONT_WEIGHT_CLASS.test(classes)
		? classes
		: `${classes} ${defaultWeight}`;
};
